import {Actions} from '@redstack/shared'
import {createFileRoute} from '@tanstack/react-router'
import {Eyebrow} from '@/components/editorial'
import {SprintPage} from '@/features/sprint/SprintPage'
import {useActiveSprintOverview} from '@/hooks/sprints'
import {useRequirePermission} from '@/hooks/useRequirePermission'

const SKELETON_SLOTS = [
	'pace',
	'closures',
	'conversion',
	'open',
	'stale',
	'mid',
	'ttc',
	'ttfa',
] as const

function SprintRouteGuard() {
	if (!useRequirePermission(Actions.PERMITS_MANAGE)) return null
	return <ActiveSprintRoute />
}

function ActiveSprintRoute() {
	const {data, isLoading, error} = useActiveSprintOverview()

	if (isLoading) {
		return (
			<div className='px-10 pt-8'>
				<Eyebrow>Current cycle</Eyebrow>
				<div className='mt-6 h-10 w-48 animate-pulse rounded-md bg-muted' />
				<div className='mt-8 grid grid-cols-1 gap-4 lg:grid-cols-3'>
					{SKELETON_SLOTS.map((slot) => (
						<div
							key={slot}
							className='h-44 animate-pulse rounded-2xl border border-border bg-card'
						/>
					))}
				</div>
			</div>
		)
	}

	if (error) {
		return (
			<div className='px-8 py-12'>
				<p className='text-destructive'>
					Failed to load cycle: {error.message}
				</p>
			</div>
		)
	}

	if (!data) return <EmptySprintState />

	return <SprintPage data={data} />
}

function EmptySprintState() {
	return (
		<div className='border-b border-border bg-background px-10 pt-7 pb-5'>
			<div className='mx-auto max-w-[1180px]'>
				<Eyebrow>Current cycle</Eyebrow>
				<h1
					className='mt-2 font-serif font-medium tracking-tight'
					style={{
						fontSize: 34,
						lineHeight: 1,
						fontVariationSettings: "'opsz' 144, 'SOFT' 100",
					}}
				>
					No cycles yet
				</h1>
				<p className='mt-2 max-w-prose text-[13px] text-muted-foreground'>
					This organization hasn't started a cycle. Contact an administrator to
					initialize the first cycle.
				</p>
			</div>
		</div>
	)
}

export const Route = createFileRoute('/sprints/current')({
	component: SprintRouteGuard,
})
